import React, { useState, useEffect, useRef } from 'react';
import { X, Search, ChevronRight, User, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { getCustomers, createPet } from '../services/api';
import { toast } from 'sonner';
import PetIcon from './PetIcon';
import { DOG_BREEDS, CAT_BREEDS } from '../data/breeds';

const SPECIES = [
  { v: 'dog', l: 'Köpek' },
  { v: 'cat', l: 'Kedi' },
  { v: 'bird', l: 'Kuş' },
  { v: 'other', l: 'Diğer' },
];

const GENDERS = [
  { v: 'male', l: 'Erkek' },
  { v: 'female', l: 'Dişi' },
];

const INIT_PET = { name: '', species: 'dog', breed: '', gender: '', color: '', birth_date: '', weight_grams: '', is_neutered: '', is_aggressive: '', microchip_no: '', notes: '' };

const QuickPetModal = ({ open, onClose, preselectedCustomer = null }) => {
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [form, setForm] = useState({ ...INIT_PET });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => {
    if (k === 'species') setForm(p => ({ ...p, species: v, breed: '' }));
    else setForm(p => ({ ...p, [k]: v }));
  };

  useEffect(() => {
    if (open) {
      setQuery(''); setResults([]); setForm({ ...INIT_PET });
      if (preselectedCustomer) {
        setSelectedCustomer(preselectedCustomer);
        setStep(2);
      } else {
        setSelectedCustomer(null);
        setStep(1);
      }
    }
  }, [open, preselectedCustomer]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await getCustomers(query); setResults(r.data); } catch (e) {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Hasta adı gerekli'); return; }
    if ((form.species === 'dog' || form.species === 'cat') && !form.breed.trim()) { toast.error('Irk seçimi zorunludur'); return; }
    setSaving(true);
    try {
      await createPet({
        customer_id: selectedCustomer.id,
        name: form.name.trim(),
        species: form.species,
        breed: form.breed.trim() || null,
        gender: form.gender || null,
        color: form.color.trim() || null,
        birth_date: form.birth_date || null,
        weight_grams: form.weight_grams ? parseInt(form.weight_grams) : null,
        is_neutered: form.is_neutered === 'yes' ? true : form.is_neutered === 'no' ? false : null,
        is_aggressive: form.is_aggressive === 'yes' ? true : form.is_aggressive === 'no' ? false : null,
        microchip_no: form.microchip_no.trim() || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`${form.name.trim()} eklendi`);
      onClose(true);
    } catch (e) { toast.error('Hasta eklenemedi'); }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="quick-pet-modal">
      <div className="absolute inset-0 bg-black/40" onClick={() => onClose(false)} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        <div className="px-4 lg:px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            {step === 2 && !preselectedCustomer && (
              <button onClick={() => setStep(1)} className="p-1 rounded-full hover:bg-gray-100 text-[#6b6de3] text-sm font-medium">
                Geri
              </button>
            )}
            <h2 className="text-base lg:text-lg font-bold text-gray-900">
              {step === 1 ? 'Müşteri Seç' : 'Yeni Hasta'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8E8E93]">{preselectedCustomer ? '' : `${step}/2`}</span>
            <button onClick={() => onClose(false)} className="p-1.5 rounded-full hover:bg-gray-100">
              <X size={22} className="text-[#8E8E93]" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-3">
          {step === 1 && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
                <Input type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)" value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && results.length === 1) { setSelectedCustomer(results[0]); setStep(2); } }}
                  className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="pet-customer-search" />
              </div>
              {results.length === 0 && query.length >= 2 && (
                <p className="text-center text-sm text-[#8E8E93] py-4">Müşteri bulunamadı</p>
              )}
              {results.map(c => (
                <button key={c.id} onClick={() => { setSelectedCustomer(c); setStep(2); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#F2F2F7] rounded-xl hover:bg-gray-200 text-left"
                  data-testid={`pet-customer-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                    {c.pets && c.pets.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.pets.map(p => (
                          <span key={p.id} className="inline-flex items-center gap-1 text-[10px] bg-white px-1.5 py-0.5 rounded-md text-gray-600">
                            <PetIcon species={p.species} size={10} /> {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={18} className="text-[#C7C7CC] shrink-0 ml-2" />
                </button>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-2">
                <User size={16} className="text-[#6b6de3]" />
                <span className="text-sm font-medium text-gray-900">{selectedCustomer?.name}</span>
                <span className="text-xs text-[#8E8E93]">({selectedCustomer?.phone})</span>
              </div>

              <div>
                <Label className="text-xs">Hasta Adı *</Label>
                <Input placeholder="Hasta adı" value={form.name} onChange={e => set('name', e.target.value)}
                  className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" autoFocus data-testid="new-pet-name" />
              </div>

              <div>
                <Label className="text-xs">Tür *</Label>
                <div className="flex gap-1.5 mt-1">
                  {SPECIES.map(s => (
                    <button key={s.v} onClick={() => set('species', s.v)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                        form.species === s.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                      }`} data-testid={`species-${s.v}`}>
                      <PetIcon species={s.v} size={13} /> {s.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">{(form.species === 'dog' || form.species === 'cat') ? 'Irk *' : 'Irk'}</Label>
                  {(form.species === 'dog' || form.species === 'cat') ? (
                    <BreedSelect
                      breeds={form.species === 'dog' ? DOG_BREEDS : CAT_BREEDS}
                      value={form.breed}
                      onChange={v => set('breed', v)}
                      testId="pet-breed-select"
                    />
                  ) : (
                    <Input placeholder="Opsiyonel" value={form.breed} onChange={e => set('breed', e.target.value)}
                      className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-breed-input" />
                  )}
                </div>
                <div>
                  <Label className="text-xs">Cinsiyet</Label>
                  <div className="flex gap-1.5 mt-1">
                    {GENDERS.map(g => (
                      <button key={g.v} onClick={() => set('gender', form.gender === g.v ? '' : g.v)}
                        className={`flex-1 h-10 rounded-xl text-xs font-medium transition-colors ${
                          form.gender === g.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`gender-${g.v}`}>{g.l}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Renk</Label>
                  <Input placeholder="Opsiyonel" value={form.color} onChange={e => set('color', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-color-input" />
                </div>
                <div>
                  <Label className="text-xs">Doğum Tarihi</Label>
                  <Input type="date" value={form.birth_date} onChange={e => set('birth_date', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-birthdate-input" />
                </div>
                <div>
                  <Label className="text-xs">Ağırlık (gram)</Label>
                  <Input type="number" placeholder="Ör: 3500" value={form.weight_grams} onChange={e => set('weight_grams', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-weight-input" />
                  {form.weight_grams && parseInt(form.weight_grams) >= 1000 && (
                    <p className="text-[10px] text-[#8E8E93] mt-0.5">{(parseInt(form.weight_grams) / 1000).toFixed(2)} kg</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">{`Kısırlaştırılmış mı?`}</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[{ v: 'yes', l: 'Evet' }, { v: 'no', l: 'Hayır' }].map(o => (
                      <button key={o.v} onClick={() => set('is_neutered', form.is_neutered === o.v ? '' : o.v)}
                        className={`flex-1 h-10 rounded-xl text-xs font-medium transition-colors ${
                          form.is_neutered === o.v ? 'bg-[#6b6de3] text-white' : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`neutered-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{`Saldırgan mı?`}</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[{ v: 'yes', l: 'Evet' }, { v: 'no', l: 'Hayır' }].map(o => (
                      <button key={o.v} onClick={() => set('is_aggressive', form.is_aggressive === o.v ? '' : o.v)}
                        className={`flex-1 h-10 rounded-xl text-xs font-medium transition-colors ${
                          form.is_aggressive === o.v ? (o.v === 'yes' ? 'bg-[#FF3B30] text-white' : 'bg-[#34C759] text-white') : 'bg-[#F2F2F7] text-gray-700'
                        }`} data-testid={`aggressive-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Mikroçip No</Label>
                  <Input placeholder="Opsiyonel" value={form.microchip_no} onChange={e => set('microchip_no', e.target.value)}
                    className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-microchip-input" />
                </div>
              </div>

              <div>
                <Label className="text-xs">Notlar</Label>
                <Input placeholder="Kronik hastalıklar, engel durumu vb." value={form.notes} onChange={e => set('notes', e.target.value)}
                  className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl mt-1" data-testid="pet-notes-input" />
              </div>
            </>
          )}
        </div>

        {step === 2 && (
          <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
            <Button onClick={handleSubmit} disabled={saving}
              className="w-full h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold"
              data-testid="save-pet-btn">
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickPetModal;

function BreedSelect({ breeds, value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = search
    ? breeds.filter(b => b.toLowerCase().includes(search.toLowerCase()))
    : breeds;

  return (
    <div className="relative mt-1" ref={ref}>
      <button type="button" onClick={() => { setOpen(!open); setSearch(''); }}
        className={`w-full h-10 px-3 rounded-xl text-sm text-left flex items-center justify-between transition-colors ${
          value ? 'bg-[#F2F2F7] text-gray-900' : 'bg-[#F2F2F7] text-[#8E8E93]'
        }`} data-testid={testId}>
        <span className="truncate">{value || 'Irk seçin'}</span>
        <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-[220px] flex flex-col overflow-hidden">
          <div className="p-1.5 border-b border-gray-100">
            <input
              type="text" autoFocus placeholder="Irk ara..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full h-8 px-2.5 text-sm rounded-lg bg-[#F2F2F7] border-0 outline-none"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <p className="text-xs text-[#8E8E93] text-center py-3">Sonuç bulunamadı</p>
            ) : filtered.map(b => (
              <button key={b} type="button"
                onClick={() => { onChange(b); setOpen(false); setSearch(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F2F2F7] transition-colors ${
                  value === b ? 'bg-[#6b6de3]/10 text-[#6b6de3] font-medium' : 'text-gray-800'
                }`} data-testid={`${testId}-option-${b}`}>
                {b}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
